import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ComboBox } from "@activecollab/components";
import PropTypes from "prop-types";
import { showErrorToast } from "~/components/Toast";
import { fetchTaskDependenciesSuggestions } from "~/store/actions/actions.taskDependencies.jsx";
import { addTask } from "~/store/actions/actions.tasks.jsx";

export const DependencySelector = ({
  options,
  dependencyId,
  placeholder,
  onSelect,
  taskListId,
  projectId,
  taskId,
  isAddNewEnabled,
  onClear,
  fetching,
  invalid,
  errorMessage,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [loadingText, setLoadingText] = useState("");

  const handleSelect = useCallback(
    (selectedId) => {
      if (selectedId === "") {
        return onClear();
      }
      const type = typeof selectedId === "number" ? "from_list" : "from_recent";

      if (onSelect) {
        return onSelect(typeof selectedId === "number" ? selectedId : parseInt(selectedId.replace(/\D/g, "")), type);
      }
    },
    [onClear, onSelect]
  );

  const handleAddNew = useCallback(
    (name) => {
      setLoading(true);
      setLoadingText(name);
      dispatch(addTask(projectId, { name, task_list_id: taskListId })).then(({ response }) => {
        if (!response?.result?.single) {
          setLoading(false);
          setLoadingText("");
          return showErrorToast(t("Failed to create task"));
        }
        dispatch(fetchTaskDependenciesSuggestions(taskId)).then(() => {
          setLoading(false);
          setLoadingText("");
        });
        onSelect(response.result.single, "created_new");
      });
    },
    [dispatch, onSelect, projectId, t, taskId, taskListId]
  );

  return (
    <ComboBox
      loading={loading || fetching}
      loadingText={loadingText}
      selected={dependencyId}
      emptyValue={isAddNewEnabled ? t("Add a Task") : ""}
      handleEmptyAction={handleAddNew}
      options={options}
      disabledInternalSort
      onChange={handleSelect}
      noResultText={t("No result")}
      placeholder={placeholder || t("Search or Add Task...")}
      invalid={invalid}
      errorMessage={errorMessage}
    />
  );
};

DependencySelector.propTypes = {
  options: PropTypes.array,
  dependencyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  onSelect: PropTypes.func,
  onClear: PropTypes.func,
  taskListId: PropTypes.number,
  projectId: PropTypes.number,
  taskId: PropTypes.number,
  isAddNewEnabled: PropTypes.bool,
  fetching: PropTypes.bool,
  invalid: PropTypes.bool,
  errorMessage: PropTypes.string,
};

DependencySelector.defaultProps = {
  options: [],
  dependencyId: 0,
  isAddNewEnabled: true,
  fetching: false,
};
